import { Box, Stack, styled } from "@mui/material";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useDataGrid } from "hooks/useDataGrid";
import { customColors } from "models/customColors";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as CSC from "SC";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { DataGrid } from "UI/DataGrid";
import { TextFieldWithEllipsis } from "UI/TextFiledWithEllipsis";
import { displayInvoiceDate, USDollar } from "utils/helpers";
import { AddNewInvoiceDialog } from "./AddNewInvoiceDialog";
import { Info, InfoSection, Label } from "./RecipientDetails.style";

// const Title = styled(Box)`
//   font-weight: 500;
// `;

const Paper = styled(Stack)`
  padding: 23px 39px;

  border-radius: 13px;
  border: 1px solid #dfdfdf;
  background-color: ${customColors.white};
`;

const columns = [
  {
    label: "Date",
    field: "invoice_date",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: displayInvoiceDate,
  },
  {
    label: "Invoice #",
    field: "id",
    xs: 1,
    sm: 1,
    md: 1,
    align: "center",
    renderer: ({ id }) => <TextFieldWithEllipsis>{id}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "total_amount",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ total_amount, state }) => (
      <Box sx={{ marginRight: "30%", textAlign: "right" }}>
        {USDollar.format(total_amount)}
      </Box>
    ),
  },
  {
    label: "Recipient",
    field: "name",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ name }) => (
      <TextFieldWithEllipsis>{name}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Phone",
    field: "phone",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ phone }) => (
      <TextFieldWithEllipsis>{phone}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Payment",
    field: "paymentStatus",
    xs: 1,
    sm: 1,
    md: 1,
    align: "center",
    renderer: ({ paymentStatus }) =>
      paymentStatus === "Paid" ? (
        <Box sx={{ color: "green" }}>Paid</Box>
      ) : (
        <Box sx={{ color: "red" }}>Required</Box>
      ),
  },
];

const mobileColumns = [
  {
    label: "Date",
    field: "invoice_date",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: displayInvoiceDate,
  },
  {
    label: "Invoice #",
    field: "id",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ id }) => <TextFieldWithEllipsis>{id}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "total_amount",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ total_amount, state }) => (
      <Box sx={{ marginRight: "30%", textAlign: "right" }}>
        {USDollar.format(total_amount)}
      </Box>
    ),
  },
  {
    label: "Recipient",
    field: "name",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ name }) => (
      <TextFieldWithEllipsis>{name}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Payment",
    field: "paymentStatus",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ paymentStatus }) =>
      paymentStatus === "Paid" ? (
        <Box sx={{ color: "green" }}>Paid</Box>
      ) : (
        <Box sx={{ color: "red" }}>Required</Box>
      ),
  },
];

const recipientColumns = [
  {
    label: "Date",
    field: "invoice_date",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: displayInvoiceDate,
  },
  {
    label: "Invoice #",
    field: "id",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ id }) => <TextFieldWithEllipsis>{id}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "total_amount",
    xs: 4,
    sm: 4,
    md: 4,
    align: "center",
    renderer: ({ total_amount }) => (
      <TextFieldWithEllipsis>
        {USDollar.format(total_amount)}
      </TextFieldWithEllipsis>
    ),
  },
  {
    label: "Payment",
    field: "paymentStatus",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ paymentStatus }) =>
      paymentStatus === "Paid" ? (
        <Box sx={{ color: "green" }}>Paid</Box>
      ) : (
        <Box sx={{ color: "red" }}>Required</Box>
      ),
  },
];

const recipientMobileColumns = [
  {
    label: "Date",
    field: "invoice_date",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: displayInvoiceDate,
  },
  {
    label: "Invoice #",
    field: "id",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
  },
  {
    label: "Amount",
    field: "total_amount",
    xs: 4,
    sm: 4,
    md: 4,
    align: "center",
    renderer: ({ total_amount }) => USDollar.format(total_amount),
  },
  {
    label: "Payment",
    field: "paymentStatus",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ paymentStatus }) =>
      paymentStatus === "Paid" ? (
        <Box sx={{ color: "green" }}>Paid</Box>
      ) : (
        <Box sx={{ color: "red" }}>Required</Box>
      ),
  },
];

export const Billing = () => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const navigate = useNavigate();
  const { userData } = useContext(GlobalContext);
  const [statistics, setStatistics] = useState(null);
  const [isNewInvoiceOpen, setIsNewInvoiceOpen] = useState(null);

  const dataGrid = useDataGrid({
    url: "/invoices",
    defaultSorter: {
      name: "invoice_date",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-billing`,
  });

  // const handleRowClick = ({ id }) => null;
  const handleRowClick = ({ id }) => navigate(`/invoice/${id}`);

  const getStatisticData = createApiCall(async () => {
    // load recipient details by recipient id
    const result = await API.get("restApi", `/invoices`, {});

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }
    setStatistics(result);
  });

  useEffect(() => {
    getStatisticData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paidInvocesQuantity = Number(
    statistics?.invoiceStatistics?.reduce((acc, next) => {
      if (next?.paymentStatus === "Paid") {
        return acc + 1;
      }
      return acc;
    }, 0) ?? 0
  );
  const unpaidInvocesQuantity = Number(
    statistics?.invoiceStatistics?.reduce((acc, next) => {
      if (next?.paymentStatus === "Required") {
        return acc + 1;
      }
      return acc;
    }, 0) ?? 0
  );

  const unpaidInvocesAmount = Number(
    statistics?.invoiceStatistics?.reduce((acc, next) => {
      if (next?.paymentStatus === "Required") {
        return (
          acc +
          Math.round(
            (next?.total ? Number(next.total) : 0) * 100
          ) /
            100
        );
      }
      return acc;
    }, 0) ?? 0
  );

  const totalClicks = Number(statistics?.totalClicks[0]?.total ?? 0);
  const paidClicks = Number(
    statistics?.itemsStatistics[0]?.referralQuantity ?? 0
  );
  const unpaidClicks = Number(
    statistics?.itemsStatistics[1]?.referralQuantity ?? 0
  );

  const isSuperAdmin = userData?.role === "Super Admin" || userData?.email === "admin@referral-stream.com";

  return (
    <CommonOutletTemplate title="Billing">
      <Paper mt={2} mb={1} sx={{ padding: "1rem" }}>
        <Stack
          direction={"row"}
          gap={2}
          alignItems={"center"}
          flexWrap={"wrap"}
          sx={{
            "@media (max-width: 768px)": {
              gap: 0.5,
            },
          }}
        >
          {/* <Title mb={1}>Statistics </Title> */}
          <InfoSection sx={{ flex: "0 0 auto" }}>
            <Info>
              <Label>Unpaid invoices: </Label>
              <strong>{unpaidInvocesQuantity}</strong> /{" "}
              {paidInvocesQuantity + unpaidInvocesQuantity}
            </Info>
          </InfoSection>
          <InfoSection sx={{ flex: "0 0 auto" }}>
            <Info>
              <Label>Debt: </Label>
              {USDollar.format(unpaidInvocesAmount)}
            </Info>
          </InfoSection>
          <InfoSection sx={{ flex: "0 0 auto" }}>
            <Info>
              <Label>Referrals without invoice:</Label>{" "}
              {totalClicks - paidClicks - unpaidClicks}
            </Info>
          </InfoSection>
        </Stack>
      </Paper>
      <DataGrid
        columns={isSuperAdmin ? columns : recipientColumns}
        mobileColumns={isSuperAdmin ? mobileColumns : recipientMobileColumns}
        handleRowClick={handleRowClick}
        {...dataGrid}
        afterSearchContent={
          isSuperAdmin && (
            <CSC.BlueButton
              variant="contained"
              color="info"
              onClick={() => setIsNewInvoiceOpen(true)}
              sx={{ flex: "0 0 auto" }}
            >
              Create New Invoice
            </CSC.BlueButton>
          )
        }
        withRefreshButton
      />
      {isSuperAdmin && (
        <AddNewInvoiceDialog
          open={!!isNewInvoiceOpen}
          handleClose={() => setIsNewInvoiceOpen(false)}
          refreshList={() => {
            getStatisticData();
            dataGrid?.loadData();
          }}
        />
      )}
    </CommonOutletTemplate>
  );
};
